import React from "react";
import type { FollowUpSheet } from "../../../api/followup/Followup.typed";
import type { Training } from "../../../api/training/Training.typed";
import { SAnalytics, SCreate } from "../../../styles/StyledMUI";
import TrainingRowTooltipButton from "../../trainings/common/TrainingRowTooltipButton";
import FollowUpForm from "./FollowUpForm";
import { useFollowUpAccess } from "./hooks/useFollowUpAccess";

type FollowUpSheetActionProps = {
  training: Training;
  /** Overrides `training.Status` when the list already knows the status code. */
  status?: string | number;
  customerName: string;
  onSaved: (sheet: FollowUpSheet) => void;
  /** Resolves to true once the sheet exists on `training` (or is about to). */
  onCreate?: () => Promise<boolean>;
  fallback?: React.ReactNode;
};

export default function FollowUpSheetAction({
  training,
  status,
  customerName,
  onSaved,
  onCreate,
  fallback = null,
}: FollowUpSheetActionProps) {
  const { canView, canEdit } = useFollowUpAccess(status ?? training.Status);
  const [open, setOpen] = React.useState(false);
  const [initialMode, setInitialMode] = React.useState<"view" | "edit">("view");

  const sheet = training.FollowUpSheet;

  const handleOpen = React.useCallback(() => {
    setInitialMode("view");
    setOpen(true);
  }, []);

  const handleCreate = React.useCallback(async () => {
    if (!onCreate) return;
    const created = await onCreate();
    if (!created) return;
    setInitialMode("edit");
    setOpen(true);
  }, [onCreate]);

  const handleClose = React.useCallback(() => setOpen(false), []);

  if (!canView) return <>{fallback}</>;

  let button: React.ReactNode;
  if (sheet) {
    button = (
      <TrainingRowTooltipButton
        title="Fiche de suivi"
        ariaLabel="Fiche de suivi"
        icon={<SAnalytics />}
        onClick={handleOpen}
        ctaVariant="accent"
      />
    );
  } else if (canEdit && onCreate) {
    button = (
      <TrainingRowTooltipButton
        title="Créer la fiche de suivi"
        ariaLabel="Créer la fiche de suivi"
        icon={<SCreate />}
        onClick={() => void handleCreate()}
        ctaVariant="accent"
      />
    );
  } else {
    button = (
      <TrainingRowTooltipButton
        title="Fiche de suivi indisponible"
        ariaLabel="Fiche de suivi indisponible"
        icon={<SAnalytics />}
        disabled
        ctaVariant="accent"
      />
    );
  }

  return (
    <>
      {button}
      {open && sheet && (
        <FollowUpForm
          open={open}
          followUpSheet={sheet}
          onClose={handleClose}
          onSave={onSaved}
          customerName={customerName}
          trainingTitle={training.Qualification ?? ""}
          canEdit={canEdit}
          initialMode={initialMode}
        />
      )}
    </>
  );
}
